import type { AcousticRoomSignal } from '../liveLessonTypes';

export type AcousticCalibrationTag =
  | 'single_speaker'
  | 'conversation'
  | 'babble'
  | 'shouting'
  | 'silence';

export type AcousticCalibrationRadarAxisKey =
  | 'loudness'
  | 'dominance'
  | 'diffuseness'
  | 'volatility'
  | 'rhythmicity'
  | 'entropy'
  | 'peakDensity'
  | 'spectralStability'
  | 'rhythmRegularity'
  | 'changeScore';

/** Werte, die Lernrakete für Fusion + Situationsentscheid nutzt (0–1) */
export type AcousticDecisionRadarAxisKey =
  | 'yamnet_speech'
  | 'yamnet_conversation'
  | 'yamnet_babble'
  | 'yamnet_shouting'
  | 'fusion_loudness'
  | 'fusion_noise'
  | 'fusion_turnTaking'
  | 'fusion_diffuseness'
  | 'fusion_coherence'
  | 'fusion_pausePulse'
  | 'fusion_crest'
  | 'fusion_peaks'
  | 'frame_single_speaker'
  | 'frame_conversation'
  | 'frame_babble'
  | 'frame_shouting'
  | 'integrated_single_speaker'
  | 'integrated_conversation'
  | 'integrated_babble'
  | 'integrated_shouting';

export interface AcousticCalibrationRadarAxis {
  key: AcousticCalibrationRadarAxisKey;
  label: string;
}

export interface AcousticCalibrationSnapshot {
  id: string;
  taggedAt: string;
  tag: AcousticCalibrationTag;
  tagLabel: string;
  note?: string;
  autoDetected: AcousticRoomSignal['detectedSituation'];
  autoConfidence: number;
  radar: Record<AcousticCalibrationRadarAxisKey, number>;
  decisionRadar: Record<AcousticDecisionRadarAxisKey, number>;
  source?: 'live' | 'file';
  sourceFileName?: string;
  situationScores: {
    single_speaker: number;
    conversation: number;
    babble: number;
    shouting: number;
  };
  levelDb: number;
  phaseFit: AcousticRoomSignal['phaseFit'];
}

export interface AcousticCalibrationStore {
  version: 1;
  updatedAt: string;
  snapshots: AcousticCalibrationSnapshot[];
}

export const ACOUSTIC_CALIBRATION_TAGS: Array<{
  key: AcousticCalibrationTag;
  label: string;
  description: string;
}> = [
  {
    key: 'single_speaker',
    label: 'Einzelsprecher',
    description: 'Eine Person spricht, Klasse überwiegend ruhig'
  },
  {
    key: 'conversation',
    label: 'Dialog',
    description: 'Wechselnde Sprecher, strukturierter Austausch'
  },
  {
    key: 'babble',
    label: 'Gemurmel',
    description: 'Viele überlappende Stimmen, diffuser Klassenraum'
  },
  {
    key: 'shouting',
    label: 'Geschrei',
    description: 'Laute, impulsive oder schreiende Lautmuster'
  },
  {
    key: 'silence',
    label: 'Stille',
    description: 'Kaum relevante Sprache oder Raumgeräusche'
  }
];

export const ACOUSTIC_CALIBRATION_RADAR_AXES: AcousticCalibrationRadarAxis[] = [
  { key: 'loudness', label: 'Lautstärke' },
  { key: 'dominance', label: 'Dominanz' },
  { key: 'diffuseness', label: 'Diffusheit' },
  { key: 'volatility', label: 'Volatilität' },
  { key: 'rhythmicity', label: 'Rhythmus' },
  { key: 'entropy', label: 'Entropie' },
  { key: 'peakDensity', label: 'Peaks' },
  { key: 'spectralStability', label: 'Spektrum' },
  { key: 'rhythmRegularity', label: 'Regelmäßigkeit' },
  { key: 'changeScore', label: 'Wechsel' }
];

export const ACOUSTIC_DECISION_RADAR_AXES: Array<{ key: AcousticDecisionRadarAxisKey; label: string }> = [
  { key: 'yamnet_speech', label: 'YAMNet Sprache' },
  { key: 'yamnet_conversation', label: 'YAMNet Dialog' },
  { key: 'yamnet_babble', label: 'YAMNet Gemurmel' },
  { key: 'yamnet_shouting', label: 'YAMNet Geschrei' },
  { key: 'fusion_loudness', label: 'Fusion Lautstärke' },
  { key: 'fusion_noise', label: 'Fusion Rauschen' },
  { key: 'fusion_turnTaking', label: 'Fusion Wechsel' },
  { key: 'fusion_diffuseness', label: 'Fusion Diffusheit' },
  { key: 'fusion_coherence', label: 'Fusion Kohärenz' },
  { key: 'fusion_pausePulse', label: 'Fusion Pausen' },
  { key: 'fusion_crest', label: 'Fusion Crest' },
  { key: 'fusion_peaks', label: 'Fusion Peaks' },
  { key: 'frame_single_speaker', label: 'Frame Einzel' },
  { key: 'frame_conversation', label: 'Frame Dialog' },
  { key: 'frame_babble', label: 'Frame Gemurmel' },
  { key: 'frame_shouting', label: 'Frame Geschrei' },
  { key: 'integrated_single_speaker', label: 'Entscheid Einzel' },
  { key: 'integrated_conversation', label: 'Entscheid Dialog' },
  { key: 'integrated_babble', label: 'Entscheid Gemurmel' },
  { key: 'integrated_shouting', label: 'Entscheid Geschrei' }
];
